import { useEffect, useId, useRef, useState } from 'react';
import { newIdempotencyKey } from '../api/client';
import type { DraftAppMutation, Problem } from '../api/types';
import { Button } from '../components/Button';
import { ProblemNotice } from '../components/ProblemNotice';
import { Spinner } from '../components/Spinner';
import { usePlatform } from '../app/platform';
import { toProblem, useAsync } from '../app/useAsync';

export interface NewMicroappDialogProps {
  workspaceId: string;
  onCreated(result: DraftAppMutation): void;
  onClose(): void;
}

/** API の APP_KEY（domain/ddl/policy.py）と同じ規則。あとから変えられない。空欄のときは検査しない（基盤が名前から作る。U1）。 */
const APP_KEY_PATTERN = /^[a-z](?:[a-z0-9]|_(?=[a-z0-9])){0,19}$/;

/**
 * Draft だけの microapp を作る（POST W/microapps:draft。C10）。UI は blank と template だけ（ZIP は残課題）。
 * 冪等キーは同じ内容の再送（通信断の後など）なら同じ値、内容を変えたら作り直す（409 idempotency_conflict を避ける）。
 */
export function NewMicroappDialog({ workspaceId, onCreated, onClose }: NewMicroappDialogProps) {
  const { api } = usePlatform();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const templates = useAsync(() => api.listDraftTemplates(workspaceId), [api, workspaceId]);
  const [name, setName] = useState('');
  const [appKey, setAppKey] = useState('');
  const [template, setTemplate] = useState('blank');
  const [running, setRunning] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [attempt, setAttempt] = useState<{ key: string; body: string } | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || dialog.open) return;
    dialog.showModal();
  }, []);

  // appKey は任意。空なら送らず、基盤が名前から作る（U1）。入れたときだけ文法を検査する。
  const appKeyInvalid = appKey !== '' && !APP_KEY_PATTERN.test(appKey);
  const ready = name.trim() !== '' && !appKeyInvalid && !running;

  const submit = async () => {
    if (!ready) return;
    const body = {
      name: name.trim(),
      ...(appKey === '' ? {} : { appKey }),
      source: template === 'blank' ? { kind: 'blank' as const } : { kind: 'template' as const, ref: template },
    };
    const fingerprint = JSON.stringify(body);
    const key = attempt !== null && attempt.body === fingerprint ? attempt.key : newIdempotencyKey();
    setAttempt({ key, body: fingerprint });
    setRunning(true);
    setProblem(null);
    try {
      const result = await api.createDraftMicroapp(workspaceId, body, key);
      onCreated(result);
    } catch (error) {
      setProblem(toProblem(error));
      setRunning(false);
    }
  };

  const close = () => {
    const dialog = dialogRef.current;
    if (dialog?.open) dialog.close();
    else onClose();
  };

  return (
    <dialog
      ref={dialogRef}
      className="dialog"
      aria-labelledby={titleId}
      onCancel={(event) => {
        if (running) event.preventDefault();
      }}
      onClose={() => {
        if (running) {
          dialogRef.current?.showModal();
          return;
        }
        onClose();
      }}
    >
      <div className="dialog-head">
        <h2 id={titleId} className="dialog-title">
          新しい microapp
        </h2>
      </div>
      <form
        className="dialog-body"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <label className="field">
          <span className="field-label">名前</span>
          <input className="input" value={name} maxLength={100} disabled={running} onChange={(event) => setName(event.target.value)} />
        </label>
        {/* appKey は上級者向け。既定は閉じておき、空のまま作れば基盤が名前から作る（U1）。 */}
        <details className="studio-advanced">
          {/* 折りたたみを閉じると入力欄も hint も見えなくなるので、文法違反だけは summary に出す
              （出さないと「作成する」が押せない理由が画面から消える）。 */}
          <summary>
            appKey を自分で決める
            {appKeyInvalid && <span className="studio-advanced-error">appKey の形式が正しくありません</span>}
          </summary>
          <label className="field">
            <span className="field-label">appKey</span>
            <input
              className="input"
              value={appKey}
              maxLength={20}
              disabled={running}
              spellCheck={false}
              autoComplete="off"
              placeholder="省略すると名前から作ります"
              onChange={(event) => setAppKey(event.target.value)}
            />
          </label>
          {/* 説明を label の外に置く（中に置くと入力欄の読み上げ名に混ざる）。 */}
          <p className="hint">
            appKey は小文字の英字で始まる英小文字・数字・_（20 文字まで）。空のままなら名前から作ります（例 Onboarding
            Flow → onboarding_flow）。あとから変えられません。
          </p>
        </details>
        <fieldset className="field studio-template-choice" disabled={running}>
          <legend className="field-label">テンプレート</legend>
          {templates.loading && <Spinner label="テンプレートを取得しています" />}
          <ProblemNotice problem={templates.problem} />
          {(templates.data?.items ?? []).map((item) => (
            <label key={item.id} className="row">
              <input
                type="radio"
                name="template"
                value={item.id}
                checked={template === item.id}
                onChange={() => setTemplate(item.id)}
              />
              <span>{item.description}</span>
              <code>{item.id}</code>
            </label>
          ))}
        </fieldset>
        <ProblemNotice problem={problem} tone={problem?.status === 409 ? 'notice' : 'error'} />
        <div className="dialog-actions">
          <Button disabled={running} onClick={close}>
            キャンセル
          </Button>
          <Button type="submit" variant="primary" disabled={!ready}>
            {running ? '作成中…' : '作成する'}
          </Button>
        </div>
      </form>
    </dialog>
  );
}
